import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCalendarDays,
    faDove,
    faCrow,
    faLocationDot,
    faMapLocationDot,
    faStar,
    faUpload
} from "@fortawesome/free-solid-svg-icons";
import Button from "../components/button";
import {EBirdChecklistByFamily, EBirdMyDataSchema} from "ebird-mydata-reader";
import {BASE_MONTHS, SelectMonthOption} from "../helpers/months";
import {getRandomDashboardHeroImage} from "../helpers/dashboardHeroImages";
import {getFamilySlug, parseObservationDateTime} from "../helpers/helpers";
import './select-time-period.scss';
import {Link} from "react-router-dom";

export type SelectTimePeriodProps = {
    rawData: EBirdMyDataSchema[],
    checklist: EBirdChecklistByFamily[],
    years: number[]|string[],
    months: number[]|string[],
    speciesImages: any,
    onSetYear: React.Dispatch<number|string>,
    onSetMonth: React.Dispatch<number|string>,
    onSetTimePeriod: (year: string, month: string) => void,
    onResetStoredData: () => void,
    onShowWrapUp: () => void,
    onShowChecklist: () => void,
    lifeList: EBirdMyDataSchema[]
}

type RecentActivity = {
    id: string,
    title: string,
    meta: string,
    dateLabel: string,
    image?: string,
    speciesDetailPath?: string
}

type DashboardIntroOption = {
    heading: string,
    description: (species: number, years: number) => React.ReactNode
}

const getSpeciesLabel = (species: number) => `${species} species`;
const renderSpeciesLabel = (species: number) => <strong>{getSpeciesLabel(species)}</strong>;
const renderYearsLabel = (years: number) => <strong>{`${years} ${years === 1 ? 'year' : 'years'}`}</strong>;

const dashboardIntroOptions: DashboardIntroOption[][] = [
    [
        {
            heading: 'Every birder has a spark bird.',
            description: species => <>You’ve already found {renderSpeciesLabel(species)}! Let’s see where your adventure has taken you.</>
        },
        {
            heading: 'Your birding journey is underway!',
            description: species => <>You’ve added {renderSpeciesLabel(species)} to your life list already.</>
        },
        {
            heading: 'Look at you go!',
            description: species => <>Every checklist tells a story! You’ve already collected {renderSpeciesLabel(species)}.</>
        },
        {
            heading: 'Your birding journey has officially taken wing!',
            description: species => <>You’ve found {renderSpeciesLabel(species)} so far.</>
        },
        {
            heading: 'The world looks different once you start noticing birds.',
            description: species => <>Here’s what your first {renderSpeciesLabel(species)} have shown you so far.</>
        }
    ],
    [
        {
            heading: "You’re really getting around!",
            description: species => <>Your life list is up to {renderSpeciesLabel(species)}.</>
        },
        {
            heading: "You’ve been keeping busy!",
            description: species => <>Over {renderSpeciesLabel(species)} have crossed your binoculars.</>
        },
        {
            heading: "You’re building quite the life list!",
            description: species => <>You’re up to {renderSpeciesLabel(species)} so far. Let’s revisit some favorite moments.</>
        },
        {
            heading: "There’s a story behind every bird.",
            description: species => <>You’ve already written {renderSpeciesLabel(species)} into yours.</>
        },
        {
            heading: "You’ve covered some ground.",
            description: species => <>Let’s see where your {renderSpeciesLabel(species)} have taken you.</>
        }
    ],
    [
        {
            heading: "Now that’s a respectable life list.",
            description: species => <>{renderSpeciesLabel(species)} and counting.</>
        },
        {
            heading: "You’ve definitely caught the birding bug.",
            description: species => <>You’re up to {renderSpeciesLabel(species)} so far. Let’s explore everything you’ve found.</>
        },
        {
            heading: 'Your binoculars have seen some miles.',
            description: species => <>You’re up to {renderSpeciesLabel(species)} so far. Let’s relive the highlights.</>
        },
        {
            heading: "You’ve made memories in a lot of habitats.",
            description: species => <>You’re up to {renderSpeciesLabel(species)} so far. Here’s where the birds have taken you.</>
        }
    ],
    [
        {
            heading: "You’ve got some history here.",
            description: (species, years) => <>Your life list now spans {renderSpeciesLabel(species)} over {renderYearsLabel(years)}.</>
        },
        {
            heading: "That’s one impressive life list.",
            description: species => <>You’re up to {renderSpeciesLabel(species)} so far. Let’s take another look.</>
        },
        {
            heading: "This isn’t your first migration.",
            description: species => <>You’re up to {renderSpeciesLabel(species)} so far. Let’s revisit your birding history.</>
        }
    ],
    [
        {
            heading: "Now that’s a life list!",
            description: species => <>{renderSpeciesLabel(species)} is worth tipping a hat to.</>
        },
        {
            heading: "You’ve birded places most people only read about.",
            description: species => <>You’ve seen {renderSpeciesLabel(species)}. Let’s revisit the journey.</>
        },
        {
            heading: 'Some people collect stamps. You collect continents.',
            description: species => <>Your {renderSpeciesLabel(species)} tell a much bigger story.</>
        },
        {
            heading: "You’ve spent years chasing birds around the world.",
            description: species => <>Here’s the story behind your {renderSpeciesLabel(species)}.</>
        },
        {
            heading: 'Every species is a memory.',
            description: species => <>Let’s revisit all {renderSpeciesLabel(species)} of yours.</>
        }
    ]
];

const getDashboardIntroTier = (species: number) => {
    if (species < 100) return dashboardIntroOptions[0];
    if (species <= 300) return dashboardIntroOptions[1];
    if (species <= 800) return dashboardIntroOptions[2];
    if (species <= 1500) return dashboardIntroOptions[3];
    return dashboardIntroOptions[4];
}

export default function SelectTimePeriod({
    rawData,
    checklist,
    years,
    months,
    speciesImages,
    onSetYear,
    onSetMonth,
    onSetTimePeriod,
    onResetStoredData,
    lifeList,
    onShowChecklist
}: SelectTimePeriodProps) {
    const latestYear = useMemo(() => {
        const numericYears = (years as Array<number|string>).filter(year => !Number.isNaN(parseInt(year as string)));
        return numericYears[0]?.toString() ?? '';
    }, [years]);
    const [year, setYear] = useState<string>('');
    const [month, setMonth] = useState<string>('null');
    const [filteredMonths, setFilteredMonths] = useState<SelectMonthOption[]>(BASE_MONTHS);
    const dashboardHeroImage = useMemo(() => {
        return getRandomDashboardHeroImage();
    }, []);

    useEffect(() => {
        if (!year && latestYear) {
            setYear(latestYear);
            onSetYear(latestYear);
        }
    }, [latestYear, onSetYear, year]);

    const dashboardYear = useMemo(() => {
        const parsedYear = parseInt(year);
        if (!Number.isNaN(parsedYear)) return parsedYear;

        const parsedLatestYear = parseInt(latestYear);
        if (!Number.isNaN(parsedLatestYear)) return parsedLatestYear;

        return Number.isNaN(parsedYear) ? undefined : parsedYear;
    }, [latestYear, year]);
    const dashboardYearLabel = useMemo(() => {
        if (!dashboardYear) return 'Your Year So Far';

        const currentYear = new Date().getFullYear();
        return dashboardYear === currentYear
            ? `Your Year So Far (${dashboardYear})`
            : `Your Year Recap (${dashboardYear})`;
    }, [dashboardYear]);
    const speciesDetailPathByTaxonomicOrder = useMemo(() => {
        const speciesDetailPaths = new Map<number, string>();

        for (const family of checklist) {
            const familySlug = getFamilySlug(family.familyName);

            for (const species of family.speciesList) {
                const detailPath = `/checklist/family/${familySlug}/species/${species.speciesCode}`;
                speciesDetailPaths.set(species.taxonomicOrder, detailPath);

                for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                    speciesDetailPaths.set(alternateTaxonomicOrder, detailPath);
                }
            }
        }

        return speciesDetailPaths;
    }, [checklist]);

    const yearObservations = useMemo(() => {
        if (!dashboardYear) return [];
        return rawData.filter(observation => observation.observedYear === dashboardYear);
    }, [dashboardYear, rawData]);

    const yearStats = useMemo(() => {
        const species = new Set<string|number>();
        const locations = new Set<string>();
        const days = new Set<string>();
        const lifers = new Set<string|number>();

        for (const observation of yearObservations) {
            const speciesKey = observation.taxonomicOrder ?? observation.commonName;
            const locationKey = observation.locationId ?? observation.location;

            if (speciesKey) {
                species.add(speciesKey);
            }
            if (locationKey) {
                locations.add(locationKey);
            }
            if (observation.date) {
                days.add(observation.date);
            }
            if (observation.isLifer && speciesKey) {
                lifers.add(speciesKey);
            }
        }

        return {
            species: species.size,
            locations: locations.size,
            days: days.size,
            lifers: lifers.size
        };
    }, [yearObservations]);

    const recentActivity = useMemo((): RecentActivity[] => {
        const latestBySpecies = new Map<string, EBirdMyDataSchema>();

        for (const observation of rawData) {
            if (!observation.date) continue;
            const key = observation.taxonomicOrder?.toString() || observation.commonName;
            if (!key) continue;

            const existingObservation = latestBySpecies.get(key);
            const observationDate = parseObservationDateTime(observation.date, observation.time)?.toMillis() ?? 0;
            const existingDate = existingObservation
                ? parseObservationDateTime(existingObservation.date, existingObservation.time)?.toMillis() ?? 0
                : 0;

            if (!existingObservation || observationDate > existingDate) {
                latestBySpecies.set(key, observation);
            }
        }

        return Array.from(latestBySpecies.entries())
            .map(([id, observation]) => {
                const date = parseObservationDateTime(observation.date, observation.time);
                const dateLabel = date?.isValid
                    ? date.toLocaleString({month: 'short', day: 'numeric', year: 'numeric'})
                    : observation.date;
                const locationBits = [observation.location, observation.county, observation.stateProvince].filter(Boolean);

                return {
                    id,
                    title: observation.commonName,
                    meta: locationBits.join(' - '),
                    dateLabel: dateLabel || '',
                    image: observation.taxonomicOrder ? speciesImages[observation.taxonomicOrder]?.thumb : undefined,
                    speciesDetailPath: observation.taxonomicOrder ? speciesDetailPathByTaxonomicOrder.get(observation.taxonomicOrder) : undefined
                };
            })
            .sort((a, b) => {
                const aObservation = latestBySpecies.get(a.id);
                const bObservation = latestBySpecies.get(b.id);
                const aDate = parseObservationDateTime(aObservation?.date, aObservation?.time)?.toMillis() ?? 0;
                const bDate = parseObservationDateTime(bObservation?.date, bObservation?.time)?.toMillis() ?? 0;
                return bDate - aDate;
            })
            .slice(0, 4);
    }, [rawData, speciesDetailPathByTaxonomicOrder, speciesImages]);

    const numYears = useMemo(() => {
        if (!years.length) return 0;
        const firstYear = parseInt(years[0] as string);
        const lastYear = parseInt(years[years.length - 1] as string);
        if (Number.isNaN(firstYear) || Number.isNaN(lastYear)) return years.length;
        return Math.max(firstYear - lastYear, 1);
    }, [years]);
    const dashboardIntro = useMemo(() => {
        const species = lifeList?.length ?? 0;
        const options = getDashboardIntroTier(species);
        const option = options[Math.floor(Math.random() * options.length)];

        return {
            heading: option.heading,
            description: option.description(species, numYears)
        };
    }, [lifeList?.length, numYears]);

    const onMonthChange = (e: ChangeEvent) => {
        setMonth((e.target as HTMLInputElement).value);
    }

    const onYearChange = (e: ChangeEvent) => {
        const selectedYear = (e.target as HTMLInputElement).value;
        setYear(selectedYear);
        setMonth('null');
        onSetYear(selectedYear);
    }

    const handleSetTimePeriod = () => {
        if (!year) return;
        onSetYear(year);
        onSetMonth(month);
        onSetTimePeriod(year, month);
    }

    const handleShowChecklist = () => {
        onShowChecklist();
    }

    const handleResetStoredData = () => {
        onResetStoredData();
    }

    useEffect(() => {
        if (!months?.length) return;

        const filteredMonths = BASE_MONTHS.filter((month: SelectMonthOption) => {
            const val: number = month.value;
            return (months as number[]).indexOf(val) > -1;
        });

        setFilteredMonths(filteredMonths);
    }, [months]);

    return (
        <section className="dashboard" aria-labelledby="dashboard-title">
            <div
                className="dashboard__hero"
                style={{'--dashboard-hero-image': `url("${dashboardHeroImage}")`} as React.CSSProperties}
            >
                <div className="dashboard__hero-content">
                    <p className="dashboard__eyebrow">Your birding data is ready to explore</p>
                    <h1 id="dashboard-title" className="dashboard__title">{dashboardIntro.heading}</h1>
                    <p className="dashboard__intro">{dashboardIntro.description}</p>

                    <div className="dashboard__filters" aria-label="Choose a time period">
                        <label className="dashboard__field">
                            <span>Show me birds from</span>
                            <select className="form-select" onChange={onYearChange} value={year}>
                                <option value="null">Select a Year</option>
                                <option value="life">Life List</option>
                                {years.map(year => (
                                    <option value={year} key={year}>{year}</option>
                                ))}
                            </select>
                        </label>

                        <label className="dashboard__field">
                            <span>During</span>
                            <select className="form-select" onChange={onMonthChange} value={month}>
                                <option value="null">All Months</option>
                                {filteredMonths.map(month => (
                                    <option value={month.value} key={month.label}>{month.label}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="dashboard__actions">
                        <Button btnClass="primary dashboard__path-btn" onClick={handleSetTimePeriod}>
                            <span className="dashboard__path-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faMapLocationDot} />
                            </span>
                            <span>
                                <strong>Explore as a Map</strong>
                                <span>See your sightings on the map</span>
                            </span>
                        </Button>
                        <Button btnClass="secondary dashboard__path-btn" onClick={handleShowChecklist}>
                            <span className="dashboard__path-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCrow} />
                            </span>
                            <span>
                                <strong>Explore Bird Families</strong>
                                <span>See birds by family and species</span>
                            </span>
                        </Button>
                    </div>
                    <Button btnClass="link dashboard__reset-btn" onClick={handleResetStoredData}>
                        <FontAwesomeIcon icon={faUpload} aria-hidden="true" />
                        Upload a new data file
                    </Button>
                </div>
            </div>

            <div className="dashboard__panel-grid">
                <section className="dashboard-panel" aria-labelledby="year-stats-title">
                    <h2 id="year-stats-title">{dashboardYearLabel}</h2>
                    <div className="dashboard-stats">
                        <div className="dashboard-stat">
                            <span className="dashboard-stat__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faDove} />
                            </span>
                            <strong>{yearStats.species}</strong>
                            <span>Species Seen</span>
                        </div>
                        <div className="dashboard-stat">
                            <span className="dashboard-stat__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faLocationDot} />
                            </span>
                            <strong>{yearStats.locations}</strong>
                            <span>{yearStats.locations === 1 ? 'Location' : 'Locations'} Birded</span>
                        </div>
                        <div className="dashboard-stat">
                            <span className="dashboard-stat__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCalendarDays} />
                            </span>
                            <strong>{yearStats.days}</strong>
                            <span>{yearStats.days === 1 ? 'Day' : 'Days'} Birded</span>
                        </div>
                        <div className="dashboard-stat">
                            <span className="dashboard-stat__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faStar} />
                            </span>
                            <strong>{yearStats.lifers}</strong>
                            <span>{yearStats.lifers === 1 ? 'Lifer' : 'Lifers'} Added</span>
                        </div>
                    </div>
                </section>

                <section className="dashboard-panel" aria-labelledby="recent-activity-title">
                    <h2 id="recent-activity-title">Recent Activity</h2>
                    <div className="dashboard-activity">
                        {recentActivity.map(activity => (
                            <div className="dashboard-activity__item" key={activity.id}>
                                <span className="dashboard-activity__image" aria-hidden="true">
                                    {activity.image ? <img src={activity.image} alt="" /> : null}
                                </span>
                                <span>
                                    {activity.speciesDetailPath ?
                                        <Link
                                            className="dashboard-activity__species-link"
                                            to={activity.speciesDetailPath}
                                        >
                                            {activity.title}
                                        </Link> :
                                        <strong>{activity.title}</strong>
                                    }
                                    <span>{activity.meta}</span>
                                </span>
                                <time>{activity.dateLabel}</time>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </section>
    )
}
